// Settings › CRM 2.0 › Customer Consent › Ask now (M1-F6) — as prototype A21t (kAskTab / kAskNew).
// Legal sends a one-off text to chosen CRM 2.0 bookings; Send sends it at once.
// The customer accepts in the Customer Portal (M3).
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Box, FormControlLabel, MenuItem, Modal, Radio, RadioGroup, Switch, TextField } from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Button from "../../../Subcomponents/Button";
import Close from "../../Icons/Close";
import { errorTost, successTost } from "../../../Services/Toast";
import {
  getConsentAsks, createConsentAsk, getCrm2Projects, getProjectWings, consentError,
} from "../../../Services/Crm2Consent";
import cssClasses from "../Settings/Crm2Settings.module.css";

const HOW = { TICK_OTP: "I agree tick + OTP", TICK: "I agree tick" };
const STAGES = [["ALL", "All stages"], ["APPLICATION_FOR_BOOKING", "Application for Booking"], ["AGREEMENT_PREPARATION", "Agreement Preparation"], ["AGREEMENT_IN_PROCESS", "Agreement in Process"], ["AGREEMENT_COMPLETE", "Agreement Complete"]];
const EDITOR_TOOLBAR = ["heading", "|", "bold", "italic", "link", "bulletedList", "numberedList", "blockQuote", "undo", "redo"];
const btnPrimary = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const btnSecondary = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const inputSx = { "& .MuiInputBase-input": { fontSize: "1.4rem" } };
const EMPTY = { title: "", content: "", projectId: "", wingId: "", stage: "ALL", list: "", how: "TICK_OTP", holdNextStep: false };

function useAllowed(key) {
  const user = useSelector((state) => state.Auth.user);
  const node = user?.roleId?.accessibleTabs?.["CRM 2.0"]?.["Settings › Customer Consent"];
  return user?.roleId?.level == 1 || !!(node?.isAccessible && node?.[key]?.isAccessible);
}

const chip = (text, color, background) => (
  <span style={{ fontSize: "1.2rem", fontFamily: "Inter-Medium", color, background, borderRadius: "1.6rem", padding: "0.2rem 1rem", whiteSpace: "nowrap" }}>{text}</span>
);

function Crm2ConsentAskNow() {
  const canEdit = useAllowed("Edit terms and Ask now");
  const [asks, setAsks] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [projects, setProjects] = useState([]);
  const [wings, setWings] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () =>
    getConsentAsks()
      .then((list) => {
        setAsks(list);
        setLoadError("");
      })
      .catch((err) => {
        console.error("[crm2][Crm2ConsentAskNow] load failed", { status: err?.response?.status, message: consentError(err).message });
        setLoadError(consentError(err).message);
      });

  useEffect(() => {
    load();
    getCrm2Projects().then(setProjects).catch((err) => console.error("[crm2][Crm2ConsentAskNow] projects load failed", { message: err.message }));
  }, []);

  useEffect(() => {
    setWings([]);
    if (form?.projectId) {
      getProjectWings(form.projectId).then(setWings).catch((err) => console.error("[crm2][Crm2ConsentAskNow] wings load failed", { message: err.message }));
    }
  }, [form?.projectId]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const send = async () => {
    const bookingIds = form.list.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    const body = {
      title: form.title, content: form.content, how: form.how, holdNextStep: form.holdNextStep,
      projectIds: bookingIds.length || !form.projectId ? [] : [form.projectId],
      wingIds: bookingIds.length || !form.wingId ? [] : [form.wingId],
      stage: bookingIds.length ? "ALL" : form.stage,
      bookingIds,
    };
    setBusy(true);
    try {
      const r = await createConsentAsk(body);
      console.log("[crm2][Crm2ConsentAskNow] ask created", { askGroupId: r.askGroupId, bookings: r.bookings, notFound: r.notFound?.length });
      successTost(`Sent to ${r.bookings} bookings${r.notFound?.length ? ` · not found: ${r.notFound.join(", ")}` : ""}`);
      setForm(null);
      setErrors({});
      load();
    } catch (err) {
      const { message, fields } = consentError(err);
      console.warn("[crm2][Crm2ConsentAskNow] ask refused", { message, fields });
      setErrors(fields);
      errorTost(message);
    } finally {
      setBusy(false);
    }
  };

  const err = (k) => errors?.[k] && <p className={cssClasses.error}>{errors[k]}</p>;

  if (loadError) return <div className={cssClasses.empty}>{loadError}</div>;
  if (!asks) return <div className={cssClasses.empty}>Loading…</div>;

  return (
    <div className={cssClasses.listWrap}>
      <p className={cssClasses.headSub}>
        Terms sent to chosen bookings, not tied to a step. The customer sees "Action needed · Please accept the updated terms" on the Customer Portal; the CRM sees a
        "Terms pending" chip on the card.
      </p>
      <div className={cssClasses.listHead}>
        <span />
        {canEdit && <Button style={btnPrimary} btnName="+ Ask now" clicked={() => { setForm({ ...EMPTY }); setErrors({}); }} />}
      </div>
      {asks.length === 0 ? (
        <div className={cssClasses.empty}>Nothing asked yet.</div>
      ) : (
        <div className={cssClasses.tableCard}>
          <table className={cssClasses.table}>
            <thead>
              <tr>{["Terms", "Sent to", "Hold the next step", "Sent", "Accepted", "Pending"].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {asks.map((a) => (
                <tr key={a.askGroupId}>
                  <td>
                    {a.title}
                    <p className={cssClasses.hint} style={{ margin: 0 }}>{a.askedByName || "—"} · {dayjs(a.createdAt).format("DD MMM YYYY, hh:mm A")} · {HOW[a.how] || a.how}</p>
                  </td>
                  <td>{a.sentTo}</td>
                  <td>{a.holdNextStep ? chip("Yes · next step waits", "#B54708", "#FFFAEB") : chip("No", "#175CD3", "#EFF8FF")}</td>
                  <td>{a.sent}</td>
                  <td>{a.accepted}</td>
                  <td>{a.pending}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!form} onClose={(_, reason) => reason !== "backdropClick" && !busy && setForm(null)} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus disableEnforceFocus>
        <Box className={`${cssClasses.modalWrapper} ${cssClasses.modalWide}`}>
          <div className={cssClasses.modalHeader}>
            <span>Ask now</span>
            <span className={cssClasses.modalClose} onClick={() => !busy && setForm(null)} role="button" tabIndex={0} aria-label="Close"><Close width="1.2rem" height="1.2rem" /></span>
          </div>
          {form && (
            <div className={cssClasses.modalContent}>
              <div>
                <p className={cssClasses.label}>Title <span className={cssClasses.required}>*</span></p>
                <TextField fullWidth size="small" placeholder="e.g. Updated refund rules" value={form.title} onChange={(e) => set({ title: e.target.value })} sx={inputSx} />
                <p className={cssClasses.hint}>Shown to the customer as the heading</p>
                {err("title")}
              </div>
              <div>
                <p className={cssClasses.label}>Terms text <span className={cssClasses.required}>*</span></p>
                <CKEditor editor={ClassicEditor} config={{ toolbar: EDITOR_TOOLBAR }} data={form.content} onChange={(event, editor) => set({ content: editor.getData() })} />
                {err("content")}
              </div>
              <div className={cssClasses.twoCol}>
                <div>
                  <p className={cssClasses.label}>Project</p>
                  <TextField select fullWidth size="small" value={form.projectId} disabled={!!form.list.trim()} onChange={(e) => set({ projectId: e.target.value, wingId: "" })} sx={inputSx}>
                    <MenuItem value="" sx={{ fontSize: "1.4rem" }}>Pick a project</MenuItem>
                    {projects.map((p) => <MenuItem key={p.projectId} value={p.projectId} sx={{ fontSize: "1.4rem" }}>{p.projectName}</MenuItem>)}
                  </TextField>
                  {!projects.length && <p className={cssClasses.hint}>No project is switched on for CRM 2.0 yet.</p>}
                </div>
                <div>
                  <p className={cssClasses.label}>Wing</p>
                  <TextField select fullWidth size="small" value={form.wingId} disabled={!form.projectId || !!form.list.trim()} onChange={(e) => set({ wingId: e.target.value })} sx={inputSx}>
                    <MenuItem value="" sx={{ fontSize: "1.4rem" }}>All wings</MenuItem>
                    {wings.map((w) => <MenuItem key={w.wingId} value={w.wingId} sx={{ fontSize: "1.4rem" }}>{w.wingName}</MenuItem>)}
                  </TextField>
                </div>
              </div>
              <div>
                <p className={cssClasses.label}>Stage</p>
                <TextField select fullWidth size="small" value={form.stage} disabled={!!form.list.trim()} onChange={(e) => set({ stage: e.target.value })} sx={inputSx}>
                  {STAGES.map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: "1.4rem" }}>{l}</MenuItem>)}
                </TextField>
                {err("stage")}
              </div>
              <div>
                <p className={cssClasses.label}>Or a list of bookings</p>
                <TextField fullWidth size="small" multiline minRows={2} placeholder="Booking IDs, separated by commas" value={form.list} onChange={(e) => set({ list: e.target.value })} sx={inputSx} />
                {err("bookings")}
              </div>
              <div>
                <p className={cssClasses.label}>How</p>
                <RadioGroup row value={form.how} onChange={(e) => set({ how: e.target.value })}>
                  {Object.entries(HOW).map(([v, l]) => <FormControlLabel key={v} value={v} control={<Radio size="small" />} label={<span className={cssClasses.radioText}>{l}</span>} />)}
                </RadioGroup>
              </div>
              <div className={cssClasses.switchLine}>
                <Switch checked={form.holdNextStep} onChange={(e) => set({ holdNextStep: e.target.checked })} />
                <span className={cssClasses.radioText}>Hold the next step until accepted</span>
              </div>
              <p className={cssClasses.hint}>Off by default so registration is not slowed. On: the customer's next step waits until they accept.</p>
            </div>
          )}
          <div className={cssClasses.modalFooter}>
            <Button style={btnSecondary} btnName="Cancel" disable={busy} clicked={() => setForm(null)} />
            <Button style={btnPrimary} btnName="Send" disable={busy} isLoading={busy} clicked={send} />
          </div>
        </Box>
      </Modal>
    </div>
  );
}

export default Crm2ConsentAskNow;
